import { useCallback, useEffect, useRef } from "react";

import { EventOrigin, Events, MeasureMode, SectionMode, track } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { Picker, selectPicker } from "features/render";
import { useConfirmCurrentModeChanges } from "features/toolMode/useConfirmCurrentModeChanges";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import CoordinatesIcon from "media/icons/coordinates.svg?react";
import LaserIcon from "media/icons/laser.svg?react";
import PointToPointIcon from "media/icons/point-to-point.svg?react";
import SectionPlaneIcon from "media/icons/section-plane.svg?react";
import TapeMeasureIcon from "media/icons/tape-measure.svg?react";
import { selectObjectMeasureEnabled } from "slices/explorer";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { Toolbar } from "../Toolbar";

interface CurrentModeToolbarProps {
    id?: string;
}

const PICKER_TO_MEASURE_MODE: Partial<Record<Picker, MeasureMode>> = {
    [Picker.OutlineLaser]: MeasureMode.Laser,
    [Picker.Coordinates]: MeasureMode.Coordinates,
    [Picker.PointLine]: MeasureMode.PointToPoint,
    [Picker.Dimension]: MeasureMode.Dimension,
};
function getMeasureMode(picker: Picker): MeasureMode | undefined {
    return PICKER_TO_MEASURE_MODE[picker];
}

const PICKER_TO_SECTION_MODE: Partial<Record<Picker, SectionMode>> = {
    [Picker.SectionPlane]: SectionMode.ClipPlane,
};
function getSectionMode(picker: Picker): SectionMode | undefined {
    return PICKER_TO_SECTION_MODE[picker];
}

/**
 * Non-flyout toolbar that shows measure tools when a measure picker is active,
 * or section tools when a section picker is active.
 */
export function CurrentModeToolbar({ id = "current-mode-toolbar" }: CurrentModeToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const currentPicker = useAppSelector(selectPicker);
    const currentPickerRef = useRef(currentPicker);
    currentPickerRef.current = currentPicker;
    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isObjectMeasureEnabled = useAppSelector(selectObjectMeasureEnabled);

    const isMeasureMode = activeToolMode === ProcoreToolMode.Measure;
    const isSectionMode = activeToolMode === ProcoreToolMode.Section;
    const isObjectMeasureVisible = isMeasureMode && isObjectMeasureEnabled;

    const enterToolMode = useEnterToolMode();
    const confirmToolMode = useConfirmCurrentModeChanges();

    const handlePickerClick = useCallback(
        (targetPicker: Picker, mode: "measure" | "clipping") => {
            if (currentPickerRef.current === targetPicker) {
                confirmToolMode();
            } else {
                enterToolMode({ picker: targetPicker });
                if (mode === "measure") {
                    const tool = getMeasureMode(targetPicker);
                    if (tool) {
                        void track(Events.measure.enabled, { mode: tool, origin: EventOrigin.Toolbar });
                    }
                } else {
                    const tool = getSectionMode(targetPicker);
                    if (tool) {
                        void track(Events.clipPlane.enabled, { mode: tool, origin: EventOrigin.Toolbar });
                    }
                }
            }
        },
        [enterToolMode, confirmToolMode],
    );

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "laser-picker",
                    icon: <LaserIcon />,
                    onClick: () => handlePickerClick(Picker.OutlineLaser, "measure"),
                    title: "Laser Tool (L)",
                    tooltip: {
                        toolName: "laser",
                        shortcutKey: "L",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.OutlineLaser,
                    disabled: false,
                    visible: isMeasureMode,
                },
            },
            {
                config: {
                    id: "coordinates-picker",
                    icon: <CoordinatesIcon />,
                    onClick: () => handlePickerClick(Picker.Coordinates, "measure"),
                    title: "Coordinates Tool (C)",
                    tooltip: {
                        toolName: "coordinates",
                        shortcutKey: "C",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.Coordinates,
                    disabled: false,
                    visible: isMeasureMode,
                },
            },
            {
                config: {
                    id: "point-to-point-picker",
                    icon: <PointToPointIcon />,
                    onClick: () => handlePickerClick(Picker.PointLine, "measure"),
                    title: "Point to Point Tool (M)",
                    tooltip: {
                        toolName: "pointToPoint",
                        shortcutKey: "M",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.PointLine,
                    disabled: false,
                    visible: isMeasureMode,
                },
            },
            {
                config: {
                    id: "object-measurement-picker",
                    icon: <TapeMeasureIcon />,
                    onClick: () => handlePickerClick(Picker.ObjectMeasurement, "measure"),
                    title: "Shortest Distance (D)",
                    tooltip: {
                        toolName: "objectMeasurement",
                        shortcutKey: "D",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.ObjectMeasurement,
                    disabled: false,
                    visible: isObjectMeasureVisible,
                },
            },
            // {
            //     config: {
            //         id: "dimension-picker",
            //         icon: <RulerIcon />,
            //         onClick: () => handleMeasurePickerClick(Picker.Dimension),
            //         title: "Dimension Tool (R)",
            //         tooltip: {
            //             toolName: "dimension",
            //             shortcutKey: "R",
            //             alwaysVisible: true,
            //             placement: "left",
            //         },
            //     },
            //     state: {
            //         active: currentPicker === Picker.Dimension,
            //         disabled: false,
            //         visible: isMeasureMode,
            //     },
            // },
            {
                config: {
                    id: "section-plane-picker",
                    icon: <SectionPlaneIcon />,
                    onClick: () => handlePickerClick(Picker.SectionPlane, "clipping"),
                    title: "Section Plane (X)",
                    tooltip: {
                        toolName: "sectionPlane",
                        shortcutKey: "X",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.SectionPlane,
                    disabled: false,
                    visible: isSectionMode,
                },
            },
        ];

        toolbarDispatch(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, handlePickerClick, currentPicker, isMeasureMode, isSectionMode, isObjectMeasureVisible]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(id, {
                "laser-picker": { active: currentPicker === Picker.OutlineLaser, visible: isMeasureMode },
                "coordinates-picker": { active: currentPicker === Picker.Coordinates, visible: isMeasureMode },
                "point-to-point-picker": { active: currentPicker === Picker.PointLine, visible: isMeasureMode },
                "object-measurement-picker": {
                    active: currentPicker === Picker.ObjectMeasurement,
                    visible: isObjectMeasureVisible,
                },
                "section-plane-picker": { active: currentPicker === Picker.SectionPlane, visible: isSectionMode },
            }),
        );
    }, [toolbarDispatch, id, currentPicker, isMeasureMode, isSectionMode, isObjectMeasureVisible]);

    return <Toolbar id={id} />;
}
